import React from "react";
import { Badge, Button, Card } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import StorePageFrame from "../userCustomDashboard/emLayout/StorePageFrame";
import {
  formatCount,
  formatDateTime,
  formatDuration,
  formatPercent,
  formatRelativeTime,
  parseSupportDate,
  readableStatus,
  statusTone,
  storeDomain,
  storeName,
} from "../support/supportFormatters";
import { useStoreWorkspace } from "./StoreWorkspaceContext";
import WorkspaceState from "./WorkspaceState";
import { workspaceModel } from "./workspaceModel";
import "./style.css";

const StatusBadge = ({ status }) => (
  <Badge status={statusTone(status)}>{readableStatus(status)}</Badge>
);

const Definition = ({ label, value, hint }) => (
  <div className="store-support-definition">
    <dt>{label}</dt>
    <dd>{value}</dd>
    {hint ? <small>{hint}</small> : null}
  </div>
);

const booleanStatus = (value, whenTrue = "READY", whenFalse = "DISCONNECTED") =>
  value === true ? whenTrue : value === false ? whenFalse : "UNKNOWN";

export const merchantExperience = (onboarding = {}, now = Date.now()) => {
  if (onboarding.merchantScreen) {
    const screen = String(onboarding.merchantScreen).toUpperCase();
    const status = String(onboarding.status || "").toUpperCase();
    const guideVisible = onboarding.showGuide ?? onboarding.guideVisible;
    return {
      screen:
        status === "UNTRACKED" || screen === "LEGACY_DASHBOARD"
          ? "Legacy dashboard / onboarding not tracked"
          : screen === "DASHBOARD" && status === "COMPLETED"
          ? guideVisible
            ? "Dashboard with completed setup guide"
            : "Standard dashboard"
          : screen === "DASHBOARD" && status === "DEFERRED"
          ? "Dashboard with setup guide"
          : screen === "ONBOARDING"
          ? `Onboarding · step ${onboarding.currentStep || 1} of 3`
          : readableStatus(onboarding.merchantScreen),
      guideVisible,
    };
  }
  const status = String(onboarding.status || "").toUpperCase();
  const completedAt = onboarding.completedAt;
  const guideExpiry = onboarding.guideExpiresAt
    ? parseSupportDate(onboarding.guideExpiresAt).getTime()
    : null;
  const guideVisible =
    onboarding.showGuide === true ||
    (Number.isFinite(guideExpiry) && guideExpiry > now);
  if (completedAt || status === "COMPLETED") {
    return {
      screen: guideVisible ? "Dashboard with completed setup guide" : "Standard dashboard",
      guideVisible,
    };
  }
  if (onboarding.deferredAt || status === "DEFERRED") {
    return { screen: "Dashboard with setup guide", guideVisible: true };
  }
  if (status === "IN_PROGRESS" || onboarding.startedAt) {
    return {
      screen: `Onboarding · step ${onboarding.currentStep || 1} of 3`,
      guideVisible: false,
    };
  }
  return { screen: "Legacy dashboard / onboarding not tracked", guideVisible: false };
};

const safePayPalLabel = (account = {}) =>
  account.description || account.payerEmail || account.merchantAccountId || account.payerId || "PayPal account";

const StoreSupportOverview = () => {
  const navigate = useNavigate();
  const workspace = useStoreWorkspace();
  const model = workspaceModel(workspace.data, workspace.shopId);
  const {
    store,
    health,
    pipeline,
    connections,
    billing,
    issues,
    recommendedActions,
    onboarding,
    disputes,
    paypalAccounts,
    operationalIncidents,
    lifecycle,
  } = model;
  const shopify = connections.shopify || {};
  const paypal = connections.paypal || {};
  const title = storeName(store);
  const hasData = Boolean(workspace.data);
  const experience = merchantExperience(onboarding);
  const primaryIssue = issues[0] || recommendedActions[0] || null;
  const disputeAttention = disputes.attention ?? disputes.responseDue;

  return (
    <StorePageFrame title={title} maxWidth="1400px" gap="16px">
      <WorkspaceState
        loading={workspace.loading}
        refreshing={workspace.refreshing}
        error={workspace.error}
        hasData={hasData}
        onRetry={() => workspace.refresh()}
      >
        <Card>
          <div className="store-support-hero">
            <div>
              <div className="store-support-hero__title">
                <h2>Support overview</h2>
                <StatusBadge status={health.status} />
              </div>
              <p role="status" aria-live="polite">{health.summary}</p>
              <span>
                {storeDomain(store)} · Store ID {store.id || workspace.shopId} · snapshot {formatDateTime(model.observedAt)}
              </span>
            </div>
            <div className="store-support-hero__actions">
              <Button onClick={() => navigate(`/store/${workspace.shopId}/orders`)}>View orders</Button>
              <Button onClick={() => navigate(`/store/${workspace.shopId}/disputes`)}>View disputes</Button>
              <Button primary onClick={() => navigate(`/store/${workspace.shopId}/actions`)}>
                Support actions
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <div className="store-support-triage">
            <div className="store-support-triage__status">
              <span>Recommended next step</span>
              <StatusBadge status={primaryIssue?.severity || (primaryIssue ? "ATTENTION" : "HEALTHY")} />
            </div>
            <div className="store-support-triage__body">
              <div>
                <h2>{primaryIssue?.message || primaryIssue?.summary || primaryIssue?.title || "No immediate action required"}</h2>
                <p>
                  {primaryIssue?.nextStep ||
                    primaryIssue?.recommendedAction ||
                    primaryIssue?.description ||
                    "Store connections, processing, billing, and dispute evidence do not currently require support intervention."}
                </p>
              </div>
              <Button onClick={() => navigate(`/store/${workspace.shopId}/actions`)}>
                Review safe actions
              </Button>
            </div>
          </div>
        </Card>

        <div className="store-support-kpis">
          <Card><div><span>Queued</span><strong>{formatCount(pipeline.queued)}</strong><small>Oldest {formatRelativeTime(pipeline.oldestQueuedAt)}</small></div></Card>
          <Card><div><span>Processing</span><strong>{formatCount(pipeline.processing)}</strong><small>{formatCount(pipeline.throughputPerMinute)} per minute</small></div></Card>
          <Card><div><span>Manual review</span><strong>{formatCount(pipeline.manualReview)}</strong><small>{formatCount(pipeline.retryWait)} retry wait · {formatCount(pipeline.blocked)} terminal</small></div></Card>
          <Card><div><span>Success · 24h</span><strong>{formatPercent(pipeline.successRate24h)}</strong><small>{formatCount(pipeline.success24h)} fulfillments</small></div></Card>
          <Card><div><span>P95 sync time</span><strong>{formatDuration(pipeline.p95SyncMs)}</strong><small>P50 {formatDuration(pipeline.p50SyncMs)}</small></div></Card>
        </div>

        <div className="store-support-two-column">
          <Card>
            <div className="store-support-card">
              <div className="store-support-heading"><h2>Connections</h2><p>Credential and runtime readiness.</p></div>
              <dl className="store-support-definition-grid">
                <Definition
                  label="Shopify app"
                  value={<StatusBadge status={shopify.status || booleanStatus(shopify.active ?? store.state, "ACTIVE", "INACTIVE")} />}
                />
                <Definition
                  label="Shopify offline session"
                  value={<StatusBadge status={shopify.sessionStatus || booleanStatus(shopify.sessionAvailable)} />}
                />
                <Definition
                  label="Shopify API credential"
                  value={<StatusBadge status={shopify.credentialStatus || booleanStatus(shopify.credentialUsable)} />}
                />
                <Definition
                  label="PayPal"
                  value={<StatusBadge status={paypal.connectionHealth || paypal.status || booleanStatus(paypal.connected)} />}
                  hint={`${formatCount(paypal.activeAccounts)} usable accounts`}
                />
                <Definition
                  label="PayPal rate limit"
                  value={<StatusBadge status={paypal.rateLimited === true ? "ATTENTION" : paypal.rateLimited === false ? "HEALTHY" : "UNKNOWN"} />}
                />
                <Definition label="Last successful sync" value={formatDateTime(pipeline.lastSuccessfulAt || store.lastSyncAt)} />
              </dl>
              <div className="store-support-account-summary">
                <strong>Connected PayPal accounts</strong>
                {paypalAccounts.length ? (
                  <ul>
                    {paypalAccounts.slice(0, 4).map((account, index) => (
                      <li key={account?.id || account?.payerId || index}>
                        <span>{safePayPalLabel(account)}</span>
                        <StatusBadge status={account?.connectionHealth || account?.status || booleanStatus(account?.usable ?? account?.active)} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No support-safe PayPal account identifiers were returned.</p>
                )}
                <Button plain onClick={() => navigate(`/store/${workspace.shopId}/configuration`)}>
                  Inspect connections
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <div className="store-support-card">
              <div className="store-support-heading"><h2>Usage billing</h2><p>Merchant-specific trial and 30-day cycle.</p></div>
              <dl className="store-support-definition-grid">
                <Definition label="Subscription" value={<StatusBadge status={billing.status || booleanStatus(billing.active, "ACTIVE", "INACTIVE")} />} />
                <Definition label="Current tier" value={billing.tier || billing.currentTier || "Not calculated"} />
                <Definition label="Orders this cycle" value={formatCount(billing.ordersProcessed ?? billing.usage)} />
                <Definition
                  label="Current amount"
                  value={
                    billing.formattedAmount ||
                    (billing.amount !== null &&
                    billing.amount !== undefined &&
                    Number.isFinite(Number(billing.amount))
                      ? `$${Number(billing.amount).toFixed(2)}`
                      : "Unknown")
                  }
                />
                <Definition label="Trial ends" value={formatDateTime(billing.trialEndsAt)} />
                <Definition label="Billing period" value={billing.periodLabel || [formatDateTime(billing.periodStart, "Unknown"), formatDateTime(billing.periodEnd, "Unknown")].join(" – ")} />
              </dl>
            </div>
          </Card>
        </div>

        <div className="store-support-two-column">
          <Card>
            <div className="store-support-card">
              <div className="store-support-heading">
                <div className="store-support-heading__with-status">
                  <h2>Merchant experience</h2>
                  <StatusBadge status={onboarding.status} />
                </div>
                <p>The page and setup state the merchant should see now.</p>
              </div>
              <dl className="store-support-definition-grid">
                <Definition label="Expected screen" value={experience.screen} />
                <Definition label="Setup guide" value={experience.guideVisible === true ? "Visible" : experience.guideVisible === false ? "Hidden" : "Unknown"} />
                <Definition label="Onboarding started" value={formatDateTime(onboarding.startedAt)} />
                <Definition label="Current step" value={onboarding.currentStep ? `${onboarding.currentStep} of 3` : "Not recorded"} />
                <Definition label="Deferred" value={formatDateTime(onboarding.deferredAt, "Not deferred")} />
                <Definition label="Completed" value={formatDateTime(onboarding.completedAt, "Not completed")} />
                <Definition label="Guide hides" value={formatDateTime(onboarding.guideExpiresAt, "Not scheduled")} />
                <Definition label="History import" value={readableStatus(onboarding.importStatus, "Not recorded")} hint={onboarding.importedOrders !== undefined ? `${formatCount(onboarding.importedOrders)} of ${formatCount(onboarding.importTotalOrders)} orders` : null} />
              </dl>
            </div>
          </Card>

          <Card>
            <div className="store-support-card">
              <div className="store-support-heading store-support-heading--action">
                <div>
                  <div className="store-support-heading__with-status">
                    <h2>PayPal disputes</h2>
                    <StatusBadge status={disputes.syncStatus || (paypal.connected === false ? "DISCONNECTED" : "UNKNOWN")} />
                  </div>
                  <p>Case load, deadlines, and PayPal reconciliation.</p>
                </div>
                <Button onClick={() => navigate(`/store/${workspace.shopId}/disputes`)}>Open cases</Button>
              </div>
              <dl className="store-support-definition-grid">
                <Definition label="Total" value={formatCount(disputes.total)} />
                <Definition label="Open" value={formatCount(disputes.open)} />
                <Definition label="Needs attention" value={formatCount(disputeAttention)} />
                <Definition label="Overdue" value={formatCount(disputes.overdue)} />
                <Definition label="Under review" value={formatCount(disputes.underReview)} />
                <Definition label="Last PayPal refresh" value={formatDateTime(disputes.lastSyncedAt)} />
              </dl>
              {disputes.syncError ? <p className="store-support-inline-error">{disputes.syncError}</p> : null}
            </div>
          </Card>
        </div>

        <div className="store-support-two-column">
          <Card>
            <div className="store-support-card">
              <div className="store-support-heading"><h2>Store identity</h2><p>Support and Shopify profile information.</p></div>
              <dl className="store-support-definition-grid">
                <Definition label="Store name" value={storeName(store)} />
                <Definition label="Owner" value={store.owner || store.shopOwner || "Not recorded"} />
                <Definition label="Contact email" value={store.email || store.customerEmail || "Not recorded"} />
                <Definition label="Country" value={store.countryName || store.country || store.countryCode || "Not recorded"} />
                <Definition label="Installed" value={formatDateTime(store.installedAt || store.creationDate || store.createdAt)} />
                <Definition
                  label="Last import"
                  value={formatDateTime(
                    store.lastImportedAt || store.lastImportAt || pipeline.lastImportAt
                  )}
                />
                <Definition label="Last merchant activity" value={formatDateTime(lifecycle.lastActivityAt)} />
                <Definition label="Last successful sync" value={formatDateTime(lifecycle.lastSyncedAt || pipeline.lastSuccessfulAt)} />
                <Definition label="Last fulfillment pull" value={formatDateTime(lifecycle.lastProcessedAtPullAt)} hint={readableStatus(lifecycle.lastPullStatus, "No pull status")} />
                <Definition label="Last pull error" value={formatDateTime(lifecycle.lastPullErrorAt, "No pull error recorded")} />
                <Definition label="Deactivated" value={formatDateTime(lifecycle.deactivatedAt, "Active / not deactivated")} hint={lifecycle.deactivationReason || null} />
              </dl>
            </div>
          </Card>

          <Card>
            <div className="store-support-card">
              <div className="store-support-heading"><h2>Recommended actions</h2><p>Evidence-backed next steps from the support service.</p></div>
              {issues.length === 0 && recommendedActions.length === 0 ? (
                <p className="store-support-empty">No immediate support action is required.</p>
              ) : (
                <ul className="store-support-issues">
                  {(issues.length ? issues : recommendedActions).map((item, index) => (
                    <li key={item?.code || item?.actionId || index}>
                      <div><StatusBadge status={item?.severity || item?.status || "INFO"} /><strong>{item?.message || item?.title || item?.summary || "Review store"}</strong></div>
                      <p>{item?.nextStep || item?.description || item?.recommendedAction || "Open Support actions for the next safe step."}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>

        {operationalIncidents.length ? (
          <Card>
            <div className="store-support-card">
              <div className="store-support-heading"><h2>Store incidents</h2><p>Current operational conditions scoped to this merchant.</p></div>
              <ul className="store-support-issues">
                {operationalIncidents.map((incident, index) => (
                  <li key={incident?.code || incident?.id || index}>
                    <div><StatusBadge status={incident?.severity || incident?.status || "ATTENTION"} /><strong>{incident?.summary || incident?.title || incident?.code || "Operational incident"}</strong></div>
                    <p>{incident?.recommendedAction || incident?.description || "Review store diagnostics before running an action."}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        ) : null}
      </WorkspaceState>
    </StorePageFrame>
  );
};

export default StoreSupportOverview;
